import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AddCreditsDialogService } from '@brand-lolly/app/billing/dialogs/add-credits-dialog/add-credits-dialog.service';
import { AutoTopUpDialogService } from '@brand-lolly/app/billing/dialogs/auto-top-up-dialog/auto-top-up-dialog.service';
import { AutoRechargeService } from '@brand-lolly/app/billing/services/auto-recharge.service';
import { CreditBalanceStateService } from '@brand-lolly/app/billing/services/credit-balance-state.service';
import { CreditPriceStateService } from '@brand-lolly/app/billing/services/credit-price-state.service';
import { GetCreditPacksService } from '@brand-lolly/app/billing/services/get-credit-packs.service';
import { GetCreditStatementService } from '@brand-lolly/app/billing/services/get-credit-statement.service';
import {
	creditDepartmentLabel,
	UNSTATED_DEPARTMENT_LABEL
} from '@brand-lolly/app/billing/types/credit-department-labels';
import { ActionPrice } from '@brand-lolly/app/billing/types/credit-prices.types';
import {
	CreditStatement,
	creditsFromMilli,
	freeAllowanceRenewsLabel,
	OutstandingCreditPurchase
} from '@brand-lolly/app/billing/types/credit-statement.types';
import {
	AutoRechargeSettings,
	CreditPack
} from '@brand-lolly/app/billing/types/mutations.types';
import {
	AUTO_TOP_UP_CAP_PHRASE,
	pausedReasonPhrase
} from '@brand-lolly/app/billing/types/card-decline.types';
import { TimeAgoPipe } from 'components';
import {
	LoButton,
	LoCard,
	LoNotice,
	LoPageHeader,
	LoPagination,
	LoProgress,
	LoSkeleton,
	LoStatus,
	LoTable,
	LoTableColumn,
	LoToastService,
	LoTooltip
} from 'lo-design-system';
import { SeatCreditRequestStateService } from '@brand-lolly/app/user-management/team/services/seat-credit-request-state.service';
import { TeamPermissionsStateService } from '@brand-lolly/app/user-management/team/services/team-permissions-state.service';
import {
	ASK_FOR_CREDITS_LABEL,
	askedForCreditsNote,
	askRefusalSentence
} from '@brand-lolly/app/user-management/team/types/credit-request.util';
import {
	CreditRequest,
	TeamAction
} from '@brand-lolly/app/user-management/team/types/team.types';
import { Subject, Subscription, takeUntil, timer } from 'rxjs';
import { AnaSettingAnchorDirective } from '@brand-lolly/components/ana-setting-anchor/ana-setting-anchor.directive';
import { AnaSettingsRevealService } from '@brand-lolly/app/_services/ana/ana-settings-reveal.service';

const UPGRADE_ROUTE = '/user-management/manage-plan';

/**
 * How many actions the free allowance is expressed in.
 *
 * The price list grows to fifteen-odd rows as the departments are cut over, and
 * a fifteen-line list of things the brand could do instead is not a ceiling it
 * can act on. Three is enough to make the figure concrete.
 */
const ALLOWANCE_TERM_COUNT = 3;

/**
 * How long each wait for the webhook is, in order.
 *
 * A purchase is accepted by Stripe and GRANTED by a webhook that arrives a
 * moment later, so the balance moves after the dialog closes and nothing on the
 * client knows when. The previous shape of this was five unconditional re-reads
 * 1.5 s apart — it ran to the end whether or not the credits had already landed,
 * and every one of them put the statement table back into skeletons. The brand
 * saw the page "refresh five times and blink".
 *
 * These are waits BETWEEN reads, they lengthen, and the sequence stops the
 * moment the balance actually moves. Worst case is five reads over ~15 s for a
 * webhook that is genuinely slow, and the common case is one.
 */
const SETTLE_WAITS_MS = [1000, 2000, 3000, 4000, 5000];

/** How long the "credits landed" acknowledgement stays on screen. */
const SETTLED_NOTE_MS = 8000;

/**
 * How often the statement's relative times are re-derived. A minute is the
 * finest unit they state, so a finer tick would redraw identical text.
 */
const WHEN_REFRESH_MS = 60_000;

/** Past this age a ledger entry reads better as a date than as "N weeks ago". */
const RELATIVE_WHEN_LIMIT_MS = 7 * 24 * 60 * 60 * 1000;

const TIME_AGO = new TimeAgoPipe();

/** One line of the statement, prepared for display. */
type StatementRow = {
	id: string;
	/** The entry's own timestamp, kept so {@link when} can be re-derived. */
	createdAt: string;
	/** Relative ("5 minutes ago"), or a short date once that stops helping. */
	when: string;
	/** The exact date and time, shown on hover over {@link when}. */
	whenExact: string;
	/** What the entry was spent on (the search's name) when it names one, else the entry's label. */
	label: string;
	/** The backend's secondary wording ("30 creators analysed"); empty when it sent none. */
	detail: string;
	/** The product area, or an em-dash when the ledger stated none. */
	area: string;
	amount: string;
	negative: boolean;
	/** The balance immediately after this entry, from the page's own figures. */
	balanceAfter: string;
	/**
	 * The label, plus its expiry when it has one. It carries the label rather
	 * than only the expiry because the column truncates arbitrary strings, and
	 * the tooltip is the only place the full text exists.
	 */
	tooltip: string;
	/**
	 * This entry was not on the previous render of this page — it arrived while
	 * the brand was looking at it. Carries a one-shot highlight so a row that
	 * appears under the reader announces itself instead of changing silently.
	 */
	arrived: boolean;
};

/**
 * One term of the subtraction that produces the headline figure.
 *
 * The page used to print "Available to spend" and "On the balance" as two
 * headline numbers side by side with nothing relating them, and a third
 * (`min(available, allowance)`) lived in the shell chip. Every one of them was
 * true and they answered different questions. These terms are how the page says
 * which question its own figure answers: the headline is the result, and each
 * term is a deduction that explains the gap.
 */
interface LedgerTerm {
	value: string;
	label: string;
	/** Rendered with a leading minus — every term but the first is a deduction. */
	deduction: boolean;
}

/**
 * One real action the remaining free allowance still covers.
 *
 * `count` carries the multiplication sign rather than a pluralised noun: the
 * labels are the statement's own ("Creator report lookup", "Creator deep dive
 * question") and there is no rule that pluralises all of them correctly, so the
 * form that needs no grammar is the form that cannot be wrong.
 */
interface AllowanceTerm {
	label: string;
	count: string;
}

/** One product area's share of the spend on the page being shown. */
interface AreaRow {
	department: string;
	area: string;
	credits: string;
	percent: number;
	/** The bar's own width, floored so a real spend is never invisible. */
	barPercent: number;
}

/** A purchase the brand still has to authenticate, prepared for display. */
interface OutstandingRow {
	id: number;
	amountCents: number;
	amount: string;
	credits: string;
	started: string;
	closes: string;
}

/**
 * The brand's credit statement: what it can spend, and the entries that account
 * for it — grants, purchases, and spend named by the action it paid for and
 * grouped by the area of the product that consumed it.
 *
 * The running balance per row is NOT recomputed from the ledger. It is walked
 * back from `balance_after_milli`, which the API guarantees against
 * `balance_before + page_sum == balance_after` — so what a brand reads on a page
 * reconciles with the balance instead of merely resembling it.
 *
 * **Two loading states, and the difference is the whole of what a purchase felt
 * like before.** `loading` is a read the brand asked for (arriving, changing
 * page) and swaps the table to skeletons. `refreshing` is a read it did not ask
 * for (the wait for a purchase to be granted) and never touches the rows: data
 * that changes under a reader has to announce itself, not blink.
 */
@Component({
	standalone: true,
	selector: 'app-credits-page',
	templateUrl: './credits-page.component.html',
	styleUrls: ['./credits-page.component.scss'],
	imports: [
		LoButton,
		LoCard,
		LoNotice,
		LoPageHeader,
		LoPagination,
		LoProgress,
		LoSkeleton,
		LoStatus,
		LoTable,
		LoTooltip,
		AnaSettingAnchorDirective
	]
})
export class CreditsPageComponent implements OnInit, OnDestroy {
	/** Starts TRUE — `ngOnInit` always reads, so an empty state before the
	 *  first response would claim there is no history before asking. */
	loading = true;
	/**
	 * Whether this person may spend the ACCOUNT's money.
	 *
	 * The balance and the statement below are theirs either way — a seat has a
	 * credit balance of its own and is shown it. What a member may not do is buy
	 * more, or change the automatic top-up that buys more on its own, because
	 * both charge the owner's card.
	 *
	 * Starts TRUE and is narrowed once the role matrix answers, the same
	 * fail-open the guard uses: the `/team` tree is not deployed everywhere, and
	 * an owner is the overwhelmingly common caller.
	 */
	canManageBilling = true;
	/**
	 * A member who may ask the master account for credits. Offered in the
	 * place "Add credits" holds for the owner, since that is the question a
	 * member reading this page has and cannot answer themselves.
	 */
	canAsk = false;
	readonly askLabel = ASK_FOR_CREDITS_LABEL;
	askInFlight = false;
	/** "Master account asked · 5 minutes ago", once they have. Replaces the control. */
	askedNote = '';
	/**
	 * Whether the member's own request has been read. Until it has, neither
	 * the control nor the note is shown — the slot keeps its size — so a
	 * member who has asked is never offered to ask again for a moment.
	 */
	requestLoaded = false;
	/** A read nobody asked for is in flight. Never swaps the table. */
	refreshing = false;
	/** The read failed: figures are unknown, which is not the same as zero. */
	failed = false;

	// ── The balance ──────────────────────────────────────────────────────
	/**
	 * The one figure that answers "what can I spend right now".
	 *
	 * NOT always `available_credits`: for an account a free ceiling governs it
	 * is `min(available, remaining)`, because the ceiling can refuse an action
	 * the balance would cover. That is the same figure the shell chip shows, so
	 * the header and this screen answer the same question with the same number
	 * — they did not before, and the gap between them read as two balances.
	 */
	availableCredits = '';
	/** The label over the headline, which changes with what governs it. */
	availableLabel = 'Available to spend';
	/** The deductions that explain the gap. Empty when there is no gap. */
	ledgerTerms: LedgerTerm[] = [];
	/** True for one animation frame after the headline figure moves. */
	balanceChanged = false;

	// ── Lifetime ─────────────────────────────────────────────────────────
	/**
	 * What of THIS brand's balance expires, and when.
	 *
	 * Null when the balance is empty: there is then nothing true to say about
	 * its lifetime, and `lifetimePolicyNote` still answers what would happen to
	 * credits arriving.
	 */
	lifetimeNote: string | null = null;
	/** Amber, and the heavier weight, once something lapses inside the warning window. */
	lifetimeUrgent = false;
	/** The rule, with the kinds it does NOT apply to named. Always shown. */
	lifetimePolicyNote = '';

	// ── The free allowance (10.7) ────────────────────────────────────────
	/** True only for an account the ceiling actually governs, or one
	 *  deliberately unbounded — never for a paying brand's dormant ceiling. */
	allowanceVisible = false;
	allowanceExhausted = false;
	allowanceTitle = '';
	allowanceBody = '';
	/**
	 * The allowance in terms the account can act on — real published prices,
	 * dearest action first. Empty whenever no price is published, which is the
	 * honest state: nothing is estimated from what the brand happened to spend
	 * last.
	 */
	allowanceTerms: AllowanceTerm[] = [];
	/** Set only when a price IS published and the allowance affords none of it. */
	allowanceTermsNote: string | null = null;
	allowancePercent = 0;
	allowanceUnboundedNote: string | null = null;

	// ── A purchase in flight, and one the bank never confirmed (10.5) ────
	outstanding: OutstandingRow[] = [];
	/** A purchase was accepted and the granting webhook has not landed yet. */
	settling = false;
	/** The credits landed. Cleared after {@link SETTLED_NOTE_MS}. */
	settledNote: string | null = null;
	/** The wait ran out. The purchase is not lost — it is just still coming. */
	settleSlow = false;

	// ── Spend by area ────────────────────────────────────────────────────
	areaRows: AreaRow[] = [];
	/** States exactly which entries the grouping above covers. */
	spendScopeNote = '';
	pageSpendCredits = '';

	// ── The entries ──────────────────────────────────────────────────────
	rows: StatementRow[] = [];
	readonly columns: LoTableColumn[] = [
		{ key: 'when', label: 'When', width: '9rem' },
		{ key: 'label', label: 'What it was' },
		{ key: 'area', label: 'Area', width: '11rem' },
		{ key: 'amount', label: 'Credits', width: '8rem' },
		{
			key: 'balanceAfter',
			label: 'Balance after',
			numeric: true,
			width: '9rem'
		}
	];
	/** Statement paging: the page shown (from 0), its size and the server total. */
	page = 0;
	pageSize = 25;
	totalEntries = 0;
	readonly pageSizes = [10, 25, 50];

	packs: CreditPack[] = [];
	/** True once the block catalog has answered — empty AFTER it has answered is
	 *  the only state worth explaining, and it disables the purchase CTA. */
	packsLoaded = false;

	// ── Automatic top-up ─────────────────────────────────────────────────
	/**
	 * The brand's automatic top-up configuration.
	 *
	 * IT LIVES ON THIS PAGE BECAUSE IT GOVERNS THIS PAGE'S FIGURE. Automatic
	 * top-up buys credits into the one platform balance the headline above
	 * reports — the same balance "Add credits" buys into, and the same one every
	 * metered action draws on. It used to sit on the Billing page inside the
	 * "API credits" block, which renders only when `GET /billing/credits`
	 * answers `has_subscription: true`, i.e. only for a brand holding a Creator
	 * Data API subscription. The backend un-keyed the configuration from that
	 * subscription on 2026-09-09 (`app/billing/credits/auto_recharge_service.py`
	 * — "the configuration is now keyed by brand … its absence refuses
	 * nothing"), and the frontend never followed, so for every brand without
	 * that one product the control simply did not exist.
	 *
	 * `GET /billing/credits/auto-recharge` answers every brand — an unconfigured
	 * one reads back `enabled: false` with a real `has_saved_card` — so there is
	 * always something true to render, and the service degrades a failed read to
	 * the same disabled default.
	 */
	autoRecharge: AutoRechargeSettings | null = null;
	/** The settings read is in flight; the row shows a placeholder until it
	 *  lands, because the dialog is seeded from these values. */
	autoRechargeLoading = true;
	/** What it does, in this brand's own numbers. Built here rather than in the
	 *  template: this page formats every figure in TypeScript and imports no
	 *  `DecimalPipe` (`avoid-template-computation`). */
	autoRechargeSummary = '';
	/** Why the server paused it, in a sentence rather than in its own codes. */
	autoRechargePausedReason: string | null = null;

	private statement: CreditStatement | null = null;
	/**
	 * What the allowance has left, kept so the actionable terms can be rebuilt
	 * when the price list lands — the two reads are independent and either can
	 * arrive first.
	 */
	private allowanceRemainingMilli: number | null = null;
	private pendingTopUp = false;
	/** The headline in milli, to notice when a read actually moved it. */
	private shownSpendableMilli: number | null = null;
	/** The ids on the previous render, so an arriving entry can be marked. */
	private shownEntryIds = new Set<string>();
	/** The balance a purchase is waiting to move away from. */
	private settleBaselineMilli: number | null = null;
	private settleAttempt = 0;
	private settleTimer: Subscription | null = null;
	/**
	 * The wait is for credits somebody ELSE bought — the master account answering
	 * a member's request — so it shows no "Payment accepted" line and no slow
	 * note, only the acknowledgement once the credits land.
	 */
	settleQuiet = false;
	private settledTimer: Subscription | null = null;
	private openRequest: CreditRequest | null = null;
	private unsubscriber = new Subject<void>();
	private settingsScope = `settings:credits:${Math.random()
		.toString(36)
		.slice(2)}`;

	constructor(
		private statementService: GetCreditStatementService,
		private creditBalanceState: CreditBalanceStateService,
		private creditPriceState: CreditPriceStateService,
		private getCreditPacksService: GetCreditPacksService,
		private addCreditsDialog: AddCreditsDialogService,
		private autoRechargeService: AutoRechargeService,
		private autoTopUpDialog: AutoTopUpDialogService,
		private route: ActivatedRoute,
		private router: Router,
		private permissionsState: TeamPermissionsStateService,
		private creditRequestState: SeatCreditRequestStateService,
		private toasts: LoToastService,
		private settingsReveal: AnaSettingsRevealService
	) {}

	ngOnInit(): void {
		this.settingsReveal.registerPage(this.settingsScope, {
			keys: ['credits.auto_top_up'],
			reload: () => this.loadAutoRecharge()
		});

		// `?topup=open` is how the shell's low-balance warning asks for the
		// purchase flow, so its button is one step from paying rather than one
		// step from a page with a button on it.
		//
		// Subscribed rather than read off the snapshot: the warning rides in the
		// shell, so it is on screen on THIS page too, and a same-route parameter
		// change does not re-run `ngOnInit`. Reading the snapshot would leave its
		// button dead exactly where the statement already is.
		this.route.queryParamMap
			.pipe(takeUntil(this.unsubscriber))
			.subscribe(params => {
				if (params.get('topup') === 'open') {
					this.pendingTopUp = true;
					this.openPendingTopUp();
				}
			});

		// The price list and the statement are independent reads and either can
		// land first, so the actionable terms are rebuilt on both.
		this.creditPriceState.snapshot$
			.pipe(takeUntil(this.unsubscriber))
			.subscribe(() => this.buildAllowanceTerms());
		this.creditPriceState.ensureLoaded();

		this.permissionsState
			.ensureLoaded()
			.pipe(takeUntil(this.unsubscriber))
			.subscribe(() => {
				this.canManageBilling = this.permissionsState.can(
					TeamAction.BILLING_MANAGE
				);
				this.canAsk =
					!this.canManageBilling &&
					this.permissionsState.can(TeamAction.CREDIT_REQUEST_RAISE);
				if (this.canAsk) {
					this.creditRequestState
						.ensureLoaded()
						.pipe(takeUntil(this.unsubscriber))
						.subscribe();
					this.creditRequestState.watch$
						.pipe(takeUntil(this.unsubscriber))
						.subscribe();
				}
			});
		// The master account answered the member's request. If it was a top-up
		// the credits land on the webhook a moment later, so the statement waits
		// for them quietly — "Payment accepted" would claim a charge this
		// member did not make, and a dismissal moves nothing to wait for.
		this.creditRequestState.resolved$
			.pipe(takeUntil(this.unsubscriber))
			.subscribe(() => this.awaitPurchase(true));
		this.creditRequestState.loaded$
			.pipe(takeUntil(this.unsubscriber))
			.subscribe(loaded => (this.requestLoaded = loaded));
		this.creditRequestState.request$
			.pipe(takeUntil(this.unsubscriber))
			.subscribe(request => {
				this.openRequest = request;
				this.refreshAskedNote();
			});

		this.loadPacks();
		this.loadAutoRecharge();
		this.load();

		// A page left open would otherwise keep saying "just now" for an entry
		// written an hour ago.
		timer(WHEN_REFRESH_MS, WHEN_REFRESH_MS)
			.pipe(takeUntil(this.unsubscriber))
			.subscribe(() => this.refreshWhen());
	}

	ngOnDestroy(): void {
		this.settingsReveal.unregisterPage(this.settingsScope);
		this.unsubscriber.next();
		this.unsubscriber.complete();
	}

	onPage(page: number): void {
		if (page === this.page) {
			return;
		}
		this.page = page;
		this.load();
	}

	onPageSize(size: number): void {
		if (size === this.pageSize) {
			return;
		}
		this.pageSize = size;
		this.page = 0;
		this.load();
	}

	trackByEntry(_: number, row: StatementRow): string {
		return row.id;
	}

	trackByArea(_: number, row: AreaRow): string {
		return row.department;
	}

	trackByOutstanding(_: number, row: OutstandingRow): number {
		return row.id;
	}

	trackByTerm(_: number, term: AllowanceTerm): string {
		return term.label;
	}

	trackByLedgerTerm(_: number, term: LedgerTerm): string {
		return term.label;
	}

	retry(): void {
		this.load();
	}

	ask(): void {
		if (this.askInFlight) {
			return;
		}
		this.askInFlight = true;
		this.creditRequestState
			.ask()
			.pipe(takeUntil(this.unsubscriber))
			.subscribe({
				next: () => (this.askInFlight = false),
				error: error => {
					this.askInFlight = false;
					this.toasts.error(askRefusalSentence(error));
				}
			});
	}

	upgrade(): void {
		this.router.navigate([UPGRADE_ROUTE], {
			queryParams: { openPlans: true }
		});
	}

	/** Buy credits. `prefillAmountCents` re-offers the amount of a purchase the
	 *  brand's bank never confirmed. */
	openAddCredits(prefillAmountCents?: number): void {
		if (!this.packs.length) {
			return;
		}
		this.addCreditsDialog
			.open({
				packs: this.packs,
				balanceCredits: this.statement?.available_credits ?? null,
				prefillAmountCents: prefillAmountCents ?? null
			})
			.pipe(takeUntil(this.unsubscriber))
			.subscribe(result => {
				if (result.purchased) {
					this.awaitPurchase();
				}
			});
	}

	/** The brand asked to look again while a slow purchase is still coming. */
	checkAgain(): void {
		this.settleSlow = false;
		this.awaitPurchase();
	}

	/**
	 * Edit automatic top-up. The dialog owns the form, the save and the
	 * "add a card first" path; this page only holds what it last read.
	 *
	 * The result IS the server's answer to the PUT, so assigning it is not a
	 * guess at what was saved — but `enabled` is not the only field that can
	 * move, and nothing on the client is told when the others do (a charge
	 * declining writes `disabled_reason`, adding a card in the Stripe portal
	 * flips `has_saved_card`). That is what {@link loadAutoRecharge} on every
	 * statement read is for; this keeps the row correct in the meantime.
	 */
	openAutoTopUp(): void {
		if (!this.autoRecharge) {
			return;
		}
		this.autoTopUpDialog
			.open({
				settings: this.autoRecharge,
				creditsPerUsd: this.creditsPerUsd
			})
			.pipe(takeUntil(this.unsubscriber))
			.subscribe(result => {
				if (result.settings) {
					this.applyAutoRecharge(result.settings);
				}
			});
	}

	/**
	 * Credits per dollar, derived from a bonus-free block. Preview only — the
	 * server recomputes the real amount; the dialog uses it to say how many
	 * credits one top-up buys.
	 */
	private get creditsPerUsd(): number | null {
		const basePack =
			this.packs.find(pack => pack.bonus_pct === 0) ?? this.packs[0];
		if (!basePack || !basePack.amount_cents) {
			return null;
		}
		return basePack.credits / (basePack.amount_cents / 100);
	}

	/**
	 * Re-read the configuration.
	 *
	 * Called on arrival AND after every statement read, including the background
	 * reads that wait for a purchase to settle — because the two things that
	 * change this row are things the client never initiated: a top-up firing
	 * (which lands as an entry on the statement) and one being refused (which
	 * pauses it and writes `disabled_reason`). A row read once in `ngOnInit`
	 * and never again would say ON to a brand whose card had just been declined.
	 */
	private loadAutoRecharge(): void {
		this.autoRechargeService
			.get()
			.pipe(takeUntil(this.unsubscriber))
			.subscribe(settings => {
				this.autoRechargeLoading = false;
				this.applyAutoRecharge(settings);
			});
	}

	private applyAutoRecharge(settings: AutoRechargeSettings): void {
		this.autoRecharge = settings;
		const money = (cents: number): string =>
			(cents / 100).toLocaleString('en-US', {
				style: 'currency',
				currency: 'USD',
				maximumFractionDigits: 0
			});
		const cap = capitalize(AUTO_TOP_UP_CAP_PHRASE);
		this.autoRechargeSummary = settings.enabled
			? `Adds ${money(
					settings.topup_amount_cents
			  )} of credits whenever the balance falls below ${settings.threshold_credits.toLocaleString(
					'en-US'
			  )}. ${cap}.`
			: `Buy credits automatically when the balance runs low, so work in flight is never refused for want of them. ${cap}.`;
		// The server sends the provider's own vocabulary on `disabled_reason`,
		// so a template binding it directly read `Paused: card_declined` to a
		// brand. See `card-decline.types.ts`.
		this.autoRechargePausedReason = pausedReasonPhrase(
			settings.disabled_reason
		);
	}

	private load(background = false): void {
		const page = this.page;
		const size = this.pageSize;
		if (background) {
			this.refreshing = true;
			// A background read is the wait for the balance to move, and the
			// thing that moved it may have been automatic top-up itself — or
			// the charge that failed and paused it. Re-read the configuration
			// alongside, so the row never contradicts the entry above it.
			this.loadAutoRecharge();
		} else {
			this.loading = true;
		}
		this.statementService
			.read(size, page * size)
			.pipe(takeUntil(this.unsubscriber))
			.subscribe(read => {
				this.loading = false;
				this.refreshing = false;
				this.failed = read.failed;
				if (read.statement === null) {
					return;
				}
				this.statement = read.statement;
				this.totalEntries = read.statement.total_entries;
				// The headline figures are account-level, not page-level, so
				// any page can feed the shell — and doing so keeps the header
				// and this screen showing the same number.
				this.creditBalanceState.publishStatement(read.statement);
				this.buildView(read.statement);
				this.openPendingTopUp();
			});
	}

	private loadPacks(): void {
		this.getCreditPacksService
			.get()
			.pipe(takeUntil(this.unsubscriber))
			.subscribe(packs => {
				this.packs = packs;
				this.packsLoaded = true;
				this.openPendingTopUp();
			});
	}

	/** Opens the purchase flow once BOTH the blocks and the balance are in. */
	private openPendingTopUp(): void {
		if (
			!this.pendingTopUp ||
			!this.packs.length ||
			this.statement === null
		) {
			return;
		}
		this.pendingTopUp = false;
		this.openAddCredits();
	}

	/**
	 * Wait for a purchase the webhook has not granted yet.
	 *
	 * Stripe accepts the charge, the credits arrive on a webhook a moment later,
	 * and nothing on the client is told when — so something has to look again.
	 * Three properties make this a wait rather than the poll it replaced:
	 *
	 * - it says so on screen from the instant the dialog closes, so the page is
	 *   never a stale figure with no explanation;
	 * - every read is a BACKGROUND read, so the statement table is not thrown
	 *   back into skeletons under a reader who is looking at it;
	 * - it stops the moment the balance moves, instead of running its five
	 *   reads out regardless.
	 */
	private awaitPurchase(quiet = false): void {
		this.settleQuiet = quiet;
		this.settleTimer?.unsubscribe();
		this.settledTimer?.unsubscribe();
		this.settledNote = null;
		this.settleSlow = false;
		this.settling = true;
		this.settleAttempt = 0;
		this.settleBaselineMilli = this.statement?.balance_milli ?? null;
		// Back to the first page: the entry the purchase writes is the newest
		// one, and leaving the brand on page 4 hides it.
		this.page = 0;
		this.load(true);
		this.scheduleSettleRead();
	}

	private scheduleSettleRead(): void {
		const wait = SETTLE_WAITS_MS[this.settleAttempt];
		if (wait === undefined) {
			// The webhook is genuinely slow. Nothing is lost and nothing was
			// double-charged; say that rather than leaving a spinner running
			// or pretending the wait succeeded.
			this.settling = false;
			// A quiet wait may be for a dismissal, where nothing was ever coming.
			this.settleSlow = !this.settleQuiet;
			return;
		}
		this.settleAttempt += 1;
		this.settleTimer = timer(wait)
			.pipe(takeUntil(this.unsubscriber))
			.subscribe(() => {
				if (!this.settling) {
					return;
				}
				this.load(true);
				this.scheduleSettleRead();
			});
	}

	/** The read came back while a purchase was settling — did it move? */
	private resolveSettlement(statement: CreditStatement): void {
		if (!this.settling) {
			return;
		}
		if (
			this.settleBaselineMilli === null ||
			statement.balance_milli === this.settleBaselineMilli
		) {
			return;
		}
		const granted = statement.balance_milli - this.settleBaselineMilli;
		this.settling = false;
		this.settleTimer?.unsubscribe();
		this.settleTimer = null;
		this.settledNote =
			granted > 0
				? `${formatCredits(
						creditsFromMilli(granted)
				  )} credits added to your balance.`
				: 'Your balance has been updated.';
		this.settledTimer = timer(SETTLED_NOTE_MS)
			.pipe(takeUntil(this.unsubscriber))
			.subscribe(() => (this.settledNote = null));
	}

	private buildView(statement: CreditStatement): void {
		this.buildBalance(statement);
		this.buildAllowance(statement);
		this.buildOutstanding(statement.awaiting_authentication);
		this.buildAreas(statement);
		this.buildRows(statement);
		this.resolveSettlement(statement);
	}

	/**
	 * The one spendable figure, and the deductions that produce it.
	 *
	 * `spendable` here means what the METER will admit, which is the balance
	 * less what running work has reserved and, for an account a free ceiling
	 * governs, no more than what that ceiling has left. Those were three
	 * separate numbers on two surfaces; this is the one of them a brand can act
	 * on, with the other two demoted to the arithmetic behind it.
	 */
	private buildBalance(statement: CreditStatement): void {
		const allowance = statement.free_allowance;
		const bounded = allowance?.bounded === true;
		const remainingMilli = allowance?.remaining_milli ?? null;
		const spendableMilli =
			bounded && remainingMilli !== null
				? Math.min(
						statement.available_milli,
						Math.max(0, remainingMilli)
				  )
				: statement.available_milli;

		this.availableCredits = formatCredits(creditsFromMilli(spendableMilli));
		this.availableLabel = bounded
			? 'Free allowance left to spend'
			: 'Available to spend';

		const terms: LedgerTerm[] = [];
		const heldMilli = statement.held_milli;
		const cappedMilli = Math.max(
			0,
			statement.available_milli - spendableMilli
		);
		if (heldMilli > 0 || cappedMilli > 0) {
			terms.push({
				value: formatCredits(statement.balance_credits),
				label: 'On the balance',
				deduction: false
			});
		}
		if (heldMilli > 0) {
			terms.push({
				value: formatCredits(creditsFromMilli(heldMilli)),
				label: 'Reserved by work running',
				deduction: true
			});
		}
		if (cappedMilli > 0) {
			terms.push({
				value: formatCredits(creditsFromMilli(cappedMilli)),
				label: 'Held back by your free allowance',
				deduction: true
			});
		}
		this.ledgerTerms = terms;

		// A figure that moves while the brand is looking at it says so once,
		// rather than being swapped in silently.
		if (
			this.shownSpendableMilli !== null &&
			this.shownSpendableMilli !== spendableMilli
		) {
			this.balanceChanged = true;
			timer(1200)
				.pipe(takeUntil(this.unsubscriber))
				.subscribe(() => (this.balanceChanged = false));
		}
		this.shownSpendableMilli = spendableMilli;

		this.buildLifetime(statement);
	}

	/**
	 * What expires, in this brand's terms rather than the policy's.
	 *
	 * `lifetime_days` is the lifetime a GRANTED credit gets, and it is the only
	 * lifetime there is — a purchase carries none, a Creator Data API allowance
	 * carries none, and neither do the credits granted before the rule landed. So
	 * a brand holding only bought or API credits is served `lifetime_days: 365`
	 * beside `next_expiry_at: null`; both are correct, and "Credits last 365 days"
	 * is false for every credit that brand holds. That sentence is what this
	 * method replaces.
	 *
	 * `next_expiry_at` is the deciding field, not `lifetime_days`: the backend
	 * computes it from the brand's OWN entries as the earliest future instant at
	 * which credits are actually lost, and answers null when none are. Credits
	 * already past their date are excluded from both it and `spendable_milli`, so
	 * "nothing expires" here can never be a balance that has quietly lapsed
	 * already.
	 */
	private buildLifetime(statement: CreditStatement): void {
		this.lifetimePolicyNote =
			`Allowance credits last ${statement.lifetime_days} days from the day ` +
			`they land. Credits you buy, and Creator Data API credits, never expire.` +
			(statement.free_allowance?.bounded
				? ' Free monthly credits expire at the end of their month.'
				: '');
		this.lifetimeUrgent = statement.expiring_soon_milli > 0;

		const spendableMilli = statement.spendable_milli;
		if (spendableMilli <= 0) {
			this.lifetimeNote = null;
			return;
		}

		const expiringMilli = statement.next_expiry_milli;
		if (!statement.next_expiry_at || expiringMilli <= 0) {
			this.lifetimeNote = 'None of the credits on your balance expire.';
			return;
		}

		const spendable = formatCredits(creditsFromMilli(spendableMilli));
		const when = shortDate(statement.next_expiry_at);
		if (expiringMilli >= spendableMilli) {
			this.lifetimeNote = `All ${spendable} credits on your balance expire on ${when}.`;
			return;
		}

		// A mixed balance. The rest either lapse on a later date or never, and the
		// statement does not say which — it publishes only the NEXT date. Naming
		// both halves is the only claim true either way; collapsing them into one
		// would promise the brand one of the two answers we do not have.
		this.lifetimeNote =
			`${formatCredits(creditsFromMilli(expiringMilli))} of your ` +
			`${spendable} credits expire on ${when}; the rest expire later or not at all.`;
	}

	private buildAllowance(statement: CreditStatement): void {
		const allowance = statement.free_allowance;
		this.allowanceVisible = false;
		this.allowanceUnboundedNote = null;
		this.allowanceRemainingMilli = null;
		this.buildAllowanceTerms();
		if (allowance === null) {
			return;
		}

		// An account deliberately given no ceiling. Worth stating, because the
		// absence is a decision somebody took and not an oversight.
		if (!allowance.bounded) {
			if (allowance.unbounded_reason !== null) {
				this.allowanceVisible = true;
				this.allowanceExhausted = false;
				this.allowancePercent = 0;
				this.allowanceTitle = 'No spending ceiling on this account';
				this.allowanceBody =
					'Metered actions are not bounded by a free allowance here.';
				this.allowanceUnboundedNote = allowance.unbounded_reason;
			}
			// A paying brand's ceiling is dormant, and a row that predates the
			// ceiling has none to report. Neither is a state to show a brand.
			return;
		}

		const ceilingMilli = allowance.ceiling_milli ?? 0;
		const remainingMilli = Math.max(0, allowance.remaining_milli ?? 0);
		// What can actually be spent: the ceiling's remainder, but never more
		// than the balance holds — the meter refuses on whichever runs out first.
		const leftMilli = Math.max(
			0,
			Math.min(statement.available_milli, remainingMilli)
		);
		const renews = freeAllowanceRenewsLabel();
		this.allowanceVisible = true;
		this.allowanceExhausted = allowance.reached || remainingMilli <= 0;
		this.allowancePercent =
			ceilingMilli > 0
				? Math.min(
						100,
						Math.round((remainingMilli / ceilingMilli) * 100)
				  )
				: 0;

		if (this.allowanceExhausted) {
			this.allowanceTitle = 'Your free credits are used up';
			this.allowanceBody = `This month's free credits are spent and renew on ${renews}. Until then creator lookups, AI search and outreach are refused — buy credits or choose a plan to keep going. Everything already collected stays where it is.`;
			return;
		}

		this.allowanceTitle = 'Your free allowance';
		this.allowanceBody = `${formatCredits(
			creditsFromMilli(leftMilli)
		)} of your ${formatCredits(
			allowance.ceiling_credits ?? creditsFromMilli(ceilingMilli)
		)} free credits left this month · renews ${renews}. Unused free credits don't carry over.`;
		this.allowanceRemainingMilli = leftMilli;
		this.buildAllowanceTerms();
	}

	/**
	 * What the remaining allowance actually covers, off the published prices.
	 *
	 * This replaced an average of the brand's own recent debits, which was the
	 * only basis available before a price list existed: honest about being an
	 * average, and still an average, so it read as wrong to the first brand
	 * whose next action cost more than its last ones. Every figure here is a
	 * real price the meter will charge.
	 *
	 * Nothing is shown at all when no price is published — an unmetered
	 * department is not a free one, and there is no figure to offer.
	 */
	private buildAllowanceTerms(): void {
		this.allowanceTerms = [];
		this.allowanceTermsNote = null;

		const remainingMilli = this.allowanceRemainingMilli;
		if (remainingMilli === null || remainingMilli <= 0) {
			return;
		}
		// A failed read leaves the list empty, and the filter below then finds
		// nothing — so a billing outage shows no terms rather than inventing
		// them, without needing a second branch to say so.
		const snapshot = this.creditPriceState.snapshot;
		if (!snapshot.loaded) {
			return;
		}

		// Only single, fixed-size actions. A zero price divides to no bound at
		// all, and a sized action's unit is a block of work rather than one
		// action, so "how many more of these" is not a well-formed question for
		// either of them.
		const countable = snapshot.prices.filter(
			(price: ActionPrice) => price.credits_milli > 0 && !price.quote
		);
		if (!countable.length) {
			return;
		}

		// Dearest first: the dearest action is the one that exhausts the
		// allowance fastest, and leading with the flattering figure would
		// describe the allowance as further from its end than it is.
		const terms = countable
			.slice()
			.sort((left, right) => right.credits_milli - left.credits_milli)
			.map(price => ({
				label: price.label,
				affordable: Math.floor(remainingMilli / price.credits_milli)
			}))
			.filter(term => term.affordable > 0)
			.slice(0, ALLOWANCE_TERM_COUNT);

		if (!terms.length) {
			this.allowanceTermsNote =
				'Not enough left for another metered action.';
			return;
		}

		this.allowanceTerms = terms.map(term => ({
			label: term.label,
			count: `${term.affordable.toLocaleString('en-US')} ×`
		}));
	}

	private buildOutstanding(purchases: OutstandingCreditPurchase[]): void {
		this.outstanding = purchases.map(purchase => ({
			id: purchase.id,
			amountCents: purchase.amount_cents,
			amount: formatMoney(purchase.amount_cents, purchase.currency),
			credits: formatCredits(purchase.credits),
			started: shortDateTime(purchase.created_at),
			closes: shortDateTime(purchase.closes_at)
		}));
	}

	/**
	 * Spend grouped by the area of the product that consumed it.
	 *
	 * Only `debit` entries count: a refund is a charge being undone and an
	 * expiry is not spend at all, so folding either in would grow an area's
	 * figure for something it never did. The grouping covers the entries on the
	 * page being shown — {@link spendScopeNote} says so, because the API offers
	 * no server-side aggregate and a total that silently described 50 of 300
	 * entries would be worse than one that names its own range.
	 */
	private buildAreas(statement: CreditStatement): void {
		const spendByDepartment = new Map<string, number>();
		let total = 0;
		for (const entry of statement.entries) {
			if (entry.kind !== 'debit') {
				continue;
			}
			const spent = Math.abs(entry.amount_milli);
			total += spent;
			spendByDepartment.set(
				entry.department,
				(spendByDepartment.get(entry.department) ?? 0) + spent
			);
		}

		this.pageSpendCredits = formatCredits(creditsFromMilli(total));
		const largest = Math.max(
			1,
			...Array.from(spendByDepartment.values(), milli => milli)
		);
		this.areaRows = Array.from(spendByDepartment.entries())
			.sort((left, right) => right[1] - left[1])
			.map(([department, milli]) => ({
				department: department || UNSTATED_DEPARTMENT_LABEL,
				area: department
					? creditDepartmentLabel(department)
					: UNSTATED_DEPARTMENT_LABEL,
				credits: formatCredits(creditsFromMilli(milli)),
				percent: total > 0 ? Math.round((milli / total) * 100) : 0,
				// Against the LARGEST area, not the total: the bars are there to
				// rank the areas against each other, and scaling to the total
				// leaves every bar but the first a stub. Floored at 2% so an
				// area that really did spend is never drawn as nothing.
				barPercent: Math.max(2, Math.round((milli / largest) * 100))
			}));

		const shown = statement.entries.length;
		if (shown === 0) {
			this.spendScopeNote = '';
			return;
		}
		const first = statement.offset + 1;
		const last = statement.offset + shown;
		this.spendScopeNote = `Entries ${first}–${last} of ${statement.total_entries}.`;
	}

	private buildRows(statement: CreditStatement): void {
		// `balance_after_milli` is the balance immediately after the NEWEST
		// entry on the page, and the entries arrive newest first — so walking
		// down the page means subtracting each entry as we pass it. The last
		// row's figure minus its own amount is `balance_before_milli`, which is
		// what makes the page account for itself.
		let runningMilli = statement.balance_after_milli;
		const seen = this.shownEntryIds;
		const next = new Set<string>();
		this.rows = statement.entries.map(entry => {
			next.add(entry.id);
			// The Area column already names the department, so a named entry
			// shows only what it was spent on.
			const label = entry.subject || entry.label;
			const row: StatementRow = {
				id: entry.id,
				createdAt: entry.created_at,
				when: relativeWhen(entry.created_at),
				whenExact: shortDateTime(entry.created_at),
				label,
				detail: entry.detail ?? '',
				area: entry.department
					? creditDepartmentLabel(entry.department)
					: '—',
				amount: formatSignedCredits(entry.amount_credits),
				negative: entry.amount_milli < 0,
				balanceAfter: formatCredits(creditsFromMilli(runningMilli)),
				tooltip: entry.expires_at
					? `${label} · expires ${shortDate(entry.expires_at)}`
					: label,
				// Only on a page that has already been rendered once — every
				// row of a first load is new, and highlighting all of them
				// would be the blink this replaced.
				arrived: seen.size > 0 && !seen.has(entry.id)
			};
			runningMilli -= entry.amount_milli;
			return row;
		});
		this.shownEntryIds = next;
	}

	private refreshWhen(): void {
		for (const row of this.rows) {
			row.when = relativeWhen(row.createdAt);
		}
		this.refreshAskedNote();
	}

	private refreshAskedNote(): void {
		this.askedNote = this.openRequest
			? askedForCreditsNote(this.openRequest)
			: '';
	}
}

/** Unsigned credits, exact to the milli the ledger holds. */
function formatCredits(credits: number): string {
	return credits.toLocaleString('en-US', {
		minimumFractionDigits: 0,
		maximumFractionDigits: 3
	});
}

/** Signed credits, so a spend reads as a spend without needing the colour. */
function formatSignedCredits(credits: number): string {
	const sign = credits < 0 ? '-' : '+';
	return `${sign}${formatCredits(Math.abs(credits))}`;
}

function formatMoney(amountCents: number, currency: string): string {
	const code = (currency || 'usd').toUpperCase();
	try {
		return new Intl.NumberFormat('en-US', {
			style: 'currency',
			currency: code
		}).format(amountCents / 100);
	} catch {
		return `${(amountCents / 100).toFixed(2)} ${code}`;
	}
}

function capitalize(text: string): string {
	return text.charAt(0).toUpperCase() + text.slice(1);
}

function shortDate(iso: string): string {
	return new Date(iso).toLocaleDateString('en-US', {
		year: 'numeric',
		month: 'short',
		day: 'numeric'
	});
}

/**
 * The statement's "When": the platform's relative wording for the last week,
 * then a short date — "3 months ago" says less than the day it happened.
 */
function relativeWhen(iso: string): string {
	const age = Date.now() - new Date(iso).getTime();
	return age < RELATIVE_WHEN_LIMIT_MS
		? TIME_AGO.transform(iso)
		: shortDate(iso);
}

function shortDateTime(iso: string): string {
	return new Date(iso).toLocaleString('en-US', {
		month: 'short',
		day: 'numeric',
		year: 'numeric',
		hour: 'numeric',
		minute: '2-digit'
	});
}
